import { JsonValue } from "../json";
import { Path } from "../types/type-system";

/**
 * Expressions are data, never code: the type checker, the path explorer and
 * migration all need to look inside them. Code escape hatches belong in a
 * step kind (a future "Run code"), whose declared outputs then automation back into
 * expressions as ordinary typed paths.
 */
export type Expr = LiteralExpr | RefExpr | VarExpr | CallExpr | LambdaExpr;

export interface LiteralExpr {
  kind: "literal";
  value: JsonValue;
}

/** A path from the automation context root, e.g. trigger.customer.email or steps.n3.status. */
export interface RefExpr {
  kind: "ref";
  path: Path;
}

/** A lambda parameter plus an optional path into it (list element access). */
export interface VarExpr {
  kind: "var";
  name: string;
  path: Path;
}

export interface CallExpr {
  kind: "call";
  fn: string;
  args: Expr[];
}

/** Only valid as an argument to list functions (any, all, count, sum, filter). */
export interface LambdaExpr {
  kind: "lambda";
  param: string;
  body: Expr;
}

export function lit(value: JsonValue): LiteralExpr {
  return { kind: "literal", value };
}

export function ref(...fields: string[]): RefExpr {
  return { kind: "ref", path: fields.map((field) => ({ field })) };
}

export function refPath(path: Path): RefExpr {
  return { kind: "ref", path };
}

export function call(fn: string, ...args: Expr[]): CallExpr {
  return { kind: "call", fn, args };
}

export function lambda(param: string, body: Expr): LambdaExpr {
  return { kind: "lambda", param, body };
}

export function v(name: string, ...fields: string[]): VarExpr {
  return { kind: "var", name, path: fields.map((field) => ({ field })) };
}

export function and(...args: Expr[]): Expr {
  if (args.length === 1 && args[0]) {
    return args[0];
  }
  return call("and", ...args);
}

export function or(...args: Expr[]): Expr {
  if (args.length === 1 && args[0]) {
    return args[0];
  }
  return call("or", ...args);
}

export function not(arg: Expr): Expr {
  return call("not", arg);
}

export function mapExpr(expr: Expr, fn: (e: Expr) => Expr): Expr {
  switch (expr.kind) {
    case "call":
      return fn({ ...expr, args: expr.args.map((a) => mapExpr(a, fn)) });
    case "lambda":
      return fn({ ...expr, body: mapExpr(expr.body, fn) });
    case "literal":
    case "ref":
    case "var":
      return fn(expr);
  }
}

export function walkExpr(expr: Expr, visit: (e: Expr) => void): void {
  visit(expr);
  if (expr.kind === "call") {
    for (const arg of expr.args) {
      walkExpr(arg, visit);
    }
  } else if (expr.kind === "lambda") {
    walkExpr(expr.body, visit);
  }
}
